"use client";
import React, { useEffect, useRef } from "react";
import Image from "next/image";

const benefits = [
  {
    title: "Lowest Shipping Cost",
    desc: "Start shipping eCommerce orders at ₹21/500gm",
    icon: "📦",
    color: "from-blue-500 to-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-100",
  },
  {
    title: "Multiple Courier Services",
    desc: "Increase distribution reach with 27+ courier options",
    icon: "🚛",
    color: "from-indigo-500 to-indigo-600",
    bg: "bg-indigo-50",
    border: "border-indigo-100",
  },
  {
    title: "Widest Pin Code Network",
    desc: "Deliver products across 29000+ pin codes",
    icon: "📍",
    color: "from-violet-500 to-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-100",
  },
  {
    title: "Swift Shipping",
    desc: "Best first-mile/last-mile with quickest pickup & delivery",
    icon: "⚡",
    color: "from-sky-500 to-sky-600",
    bg: "bg-sky-50",
    border: "border-sky-100",
  },
  {
    title: "30% Less RTO",
    desc: "Reduce RTO losses and scale your eCommerce profits",
    icon: "📉",
    color: "from-emerald-500 to-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
  },
  {
    title: "One-day COD Remittance",
    desc: "Early COD payments for uninterrupted cash flow",
    icon: "💰",
    color: "from-amber-500 to-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-100",
  },
  {
    title: "Enhanced Post-shipment",
    desc: "Get automated shipment updates in your inbox",
    icon: "📲",
    color: "from-rose-500 to-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-100",
  },
  {
    title: "Branded Tracking Page",
    desc: "Drive post-purchase engagement with customers",
    icon: "🧾",
    color: "from-teal-500 to-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-100",
  },
  {
    title: "24x7 Support",
    desc: "Non-stop eCommerce shipping with prompt support",
    icon: "🕒",
    color: "from-orange-500 to-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-100",
  },
];

const images = [
  "/image/hindware.png",
  "/image/Karcher-Logo.png",
  ...Array.from({ length: 49 }, (_, i) => `/image/${i + 1}.png`).filter(
    (_, i) => i + 1 !== 46
  ),
];

const coreValues = [
  {
    title: "Customer Centricity",
    desc: "Nurturing strong bonds & delivering an outstanding customer experience is the hallmark of our services. The customer is at the heart of our business ecosystem.",
    num: "01",
  },
  {
    title: "Innovation",
    desc: "With constant innovation and an endless chain of original ideas, we aim to revolutionise the industrial landscape through robust and refined logistics solutions.",
    num: "02",
  },
  {
    title: "Empathy",
    desc: "Showing empathy enables us to anticipate the needs, fears, and difficulties of others and put in genuine effort to address them.",
    num: "03",
  },
  {
    title: "Integrity",
    desc: "Powered by integrity and driven by strong morals, India's most trusted logistics brand values ethics over anything else.",
    num: "04",
  },
];

const logisticsCards = [
  {
    title: "Nationwide Parcel Delivery with Smart Fleet Support",
    desc: "At SureShip, we leverage a smart fleet network to ensure your packages reach their destination safely and on time. From small parcels to bulk shipments, we deliver across major cities like Mumbai, Delhi, Bangalore, Kolkata, and Hyderabad. Our advanced tracking system and reliable delivery force make sure every delivery is handled with care and efficiency—covering thousands of kilometers daily for customer satisfaction.",
    img: "/image/e (81).jpg",
    tag: "Logistics & Parcel Delivery",
  },
  {
    title: "Logistics You Can Trust—From First Mile to Last",
    desc: "SureShip delivers smarter logistics for a fast-moving world. Our AI-powered fleet and seamless delivery infrastructure guarantee efficient movement of goods—whether it's a document or a bulk shipment. Operating across major Indian cities like Mumbai, Delhi, Bangalore, Kolkata, and Hyderabad, we ensure real-time visibility, secure transit, and timely arrivals. With a focus on technology and reliability, SureShip is your trusted logistics partner from start to finish.",
    img: "/image/e (82).jpg",
    tag: "Logistics & Parcel Delivery",
    reverse: true,
  },
  {
    title: "Financial Support & Scalable Logistics for Growing Businesses",
    desc: "At SureShip, we understand the importance of cost-efficient logistics. That's why we offer flexible payment plans and tailored solutions to support startups and SMEs. With our wide delivery network and transparent pricing, businesses can focus on growth while we handle the logistics—from documents to full-scale cargo, across every major Indian city.",
    img: "/image/e (83).jpg",
    tag: "Smart Logistics & Business Support",
  },
  {
    title: "AI-Powered Logistics Built for Speed and Accuracy",
    desc: "SureShip combines AI technology and intelligent routing to deliver smarter, faster logistics. Whether it's real-time tracking, route optimization, or automated dispatch, our systems ensure flawless execution. Serving metros like Mumbai, Delhi, Bangalore, and beyond, we make supply chain management effortless and responsive—helping you deliver promises on time.",
    img: "/image/e (84).jpg",
    tag: "Smart Logistics & Business Support",
    reverse: true,
  },
];

export default function Page() {
  return (
    <div className="bg-white text-gray-900 font-sans">
      {/* ── BUSINESS BENEFITS ── */}
      <section className="relative overflow-hidden pt-40 pb-24 px-4 md:px-16">
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#1e40af 1px, transparent 1px), linear-gradient(to right, #1e40af 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] text-blue-600 uppercase bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full mb-4">
              Why Choose SureShip
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              Business{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Benefits
              </span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
              Say hi to a scaling business and wave goodbye to missed sales,
              limited reach, delayed deliveries, and a poor remittance cycle.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden rounded-2xl border ${benefit.border} ${benefit.bg} p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center text-xl shadow-sm`}
                  >
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOGISTICS SECTIONS ── */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-20 px-4 md:px-16">
        <div className="max-w-7xl mx-auto space-y-24">
          {logisticsCards.map((card, i) => (
            <div key={i} className="group">
              {/* Section tag */}
              {(i === 0 || i === 2) && (
                <div className="flex items-center gap-3 mb-10">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent to-yellow-400 max-w-[60px]" />
                  <span className="text-xs font-bold tracking-[0.15em] uppercase text-yellow-600 bg-yellow-50 border border-yellow-200 px-4 py-1.5 rounded-full">
                    {card.tag}
                  </span>
                  <div className="h-px flex-1 bg-yellow-200" />
                </div>
              )}

              <div
                className={`grid md:grid-cols-2 gap-10 items-center ${card.reverse ? "md:grid-flow-dense" : ""}`}
              >
                {/* Text */}
                <div
                  className={`${card.reverse ? "md:col-start-2" : ""} space-y-4`}
                >
                  <div className="w-10 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 rounded-full" />
                  <h4 className="text-2xl md:text-3xl font-bold text-blue-900 leading-snug">
                    {card.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed text-base">
                    {card.desc}
                  </p>
                </div>

                {/* Image */}
                <div
                  className={`${card.reverse ? "md:col-start-1" : ""} rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/5`}
                >
                  <Image
                    src={card.img}
                    alt={card.title}
                    width={800}
                    height={500}
                    className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── BRAND PARTNERS / MARQUEE ── */}
      <section className="bg-white py-20 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 mb-10 text-center">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-400">
            Trusted by 50+ leading brands
          </span>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold text-gray-800">
            Partners of{" "}
            <span className="text-blue-600">SureShip</span>
          </h2>
        </div>

        <div className="relative overflow-hidden">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          <div className="flex min-w-max animate-marquee whitespace-nowrap">
            {[...images, ...images].map((logo, idx) => (
              <div
                key={`${logo}-${idx}`}
                className="relative h-24 w-52 flex-shrink-0 mx-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <Image
                  src={logo}
                  alt={`brand-logo-${idx}`}
                  fill
                  className="object-contain p-4"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ── */}
      <section className="py-24 px-4 md:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] text-blue-600 uppercase bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full mb-4">
              What We Stand For
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0a0055]">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {coreValues.map((val, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50/50 p-8 hover:shadow-lg hover:border-blue-200 transition-all duration-300"
              >
                <span className="absolute top-6 right-6 text-6xl font-black text-blue-50 group-hover:text-blue-100 transition-colors select-none">
                  {val.num}
                </span>
                <div className="w-8 h-1 bg-blue-400 rounded-full mb-5" />
                <h4 className="text-xl font-bold text-blue-900 mb-3">
                  {val.title}
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {val.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NAVIGATION / COMPARISON the ── */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-24 px-4 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              Navigate your logistics journey{" "}
              <span className="text-blue-400">your way</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* BYOC in */}
            <div className="relative rounded-2xl bg-white/5 border border-white/10 p-8 flex flex-col">
              <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/10">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-lg">
                  🚚
                </div>
                <h3 className="font-bold text-lg text-white">
                  Bring your own Carriers (BYOC)
                </h3>
              </div>
              <ul className="space-y-3 flex-1">
                {[
                  "Limited to one courier, reducing flexibility across regions.",
                  "Restricted visibility and control over serviceable pin codes.",
                  "Manual rate negotiations can lead to inconsistent pricing.",
                  "Delayed COD remittance, managed solely by your courier.",
                  "Must coordinate directly with courier for delivery and weight disputes.",
                  "No added protection — solely dependent on courier for damages/losses.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                    <span className="mt-0.5 w-4 h-4 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 flex-shrink-0 text-xs">
                      ✕
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <button className="mt-8 w-full py-3 px-6 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white font-semibold transition-colors">
                Request A Call Back
              </button>
            </div>

            {/* SureShip */}
            <div className="relative rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 p-8 flex flex-col shadow-2xl shadow-blue-900/40">
              <div className="absolute top-4 right-4 text-[10px] font-bold tracking-widest uppercase bg-white/20 text-white px-3 py-1 rounded-full">
                Recommended
              </div>
              <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/20">
                <div className="flex items-center gap-2">
                  <img
                    src="/image/e (4) (1).png"
                    alt="sureship"
                    className="h-8"
                  />
                </div>
                <h3 className="font-bold text-lg text-white">
                  SureShip carrier aggregator
                </h3>
              </div>
              <ul className="space-y-3 flex-1">
                {[
                  "Partner with India's leading courier provider for reliable delivery.",
                  "Reach over 29,000+ pin codes across the country with ease.",
                  "Enjoy competitive shipping rates starting from just ₹19 per 500g.",
                  "Fast COD settlements within 2 working days (D+2).",
                  "Get dedicated support for pickup scheduling and shipment queries.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-blue-50">
                    <span className="mt-0.5 w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-white flex-shrink-0 text-xs">
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="https://app.sureship.in"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 w-full py-3 px-6 rounded-xl bg-white text-blue-700 font-bold text-center hover:bg-blue-50 transition-colors shadow-lg"
              >
                Start with Sureship Logistics →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
